import { palette } from '../../styles/tokens';
import { formatPhone, canonicalPhone } from '../../utils/phone';

/**
 * PhoneInput — Canadian phone-number input primitive.
 *
 * Emits `(xxx) xxx-xxxx` via onChange. Raw user keystrokes are filtered to
 * digits and re-formatted live; the parent always sees the formatted string.
 * On blur, the canonical form is emitted (same as live during typing since
 * the formatter is deterministic).
 *
 * Uses type="tel" + inputMode="tel" to surface the numeric keypad on
 * mobile browsers and dedicated tel-input UI on desktop (where supported).
 * The actual format enforcement is the JS-side filter; the browser's own
 * tel-input semantics don't validate Canadian shape.
 *
 * Rule #34 analog for phone: storage format is canonical `(xxx) xxx-xxxx`.
 * Legacy data stored as raw digits or other formats is auto-upgraded on
 * next edit (the formatter handles any input shape).
 */

export interface PhoneInputProps {
  value: string;                       // current value (canonical or partial)
  onChange: (value: string) => void;   // emits formatted value live
  onBlur?: () => void;                 // fires after canonicalize-on-blur
  placeholder?: string;                // default "(555) 123-4567"
  disabled?: boolean;
  error?: string;                      // optional error message rendered below
  ariaLabel?: string;
  autoFocus?: boolean;
  id?: string;
  name?: string;
  className?: string;
  style?: React.CSSProperties;
}

const WRAPPER: React.CSSProperties = {
  minWidth: 0,
};

const INPUT_BASE: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
};

const INPUT_ERROR: React.CSSProperties = {
  borderColor: palette.red,
};

const INPUT_DISABLED: React.CSSProperties = {
  background: palette.g50,
  color: palette.g400,
  cursor: 'not-allowed',
};

const ERROR_TEXT: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: palette.red,
};

export function PhoneInput({
  value,
  onChange,
  onBlur,
  placeholder = '(555) 123-4567',
  disabled,
  error,
  ariaLabel,
  autoFocus,
  id,
  name,
  className,
  style,
}: PhoneInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Live-format every keystroke. formatPhone strips non-digits and
    // truncates to 10, so users pasting garbage or international numbers
    // get the canonical shape immediately.
    onChange(formatPhone(e.target.value));
  };

  const handleBlur = () => {
    const canonical = canonicalPhone(value);
    if (canonical !== value) onChange(canonical);
    onBlur?.();
  };

  const inputStyle: React.CSSProperties = {
    ...INPUT_BASE,
    ...(error ? INPUT_ERROR : {}),
    ...(disabled ? INPUT_DISABLED : {}),
  };

  return (
    <div className={className} style={{ ...WRAPPER, ...style }}>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        autoFocus={autoFocus}
        id={id}
        name={name}
        style={inputStyle}
      />
      {error && <div style={ERROR_TEXT}>{error}</div>}
    </div>
  );
}
